import React from 'react';
import { Typography } from 'antd';
import classNames from 'classnames';
import RMCard from './RMCard';
import './RMCard.scss';
import InventoryCardDropdown from './InventoryCardDropdown';

const { Text } = Typography;

/**
 * InventoryCard Component
 *
 * Extends RMCard with content array rendering logic for inventory use cases.
 */
export const InventoryCard = ({
    title,
    content = null,
    children,
    variant = null,
    className = '',
    isExpanded = true,
    onIgnoreOptimizations,
    onMarkAsReady,
    showEllipsis = true,
    ...props
}) => {
    // Render content array
    const renderContent = () => {
        if (!Array.isArray(content)) {
            return null;
        }

        // Filter content based on isExpanded state
        const filteredContent = isExpanded ? content : content.filter((item) => item.type === 'primary');

        const renderItem = (item, idx) => {
            const { text, type, inline: itemInline, continuation } = item;
            const itemClassName = classNames('rm-card-item', {
                inline: itemInline,
                block: !itemInline,
                'with-padding-top-sm': type !== 'muted',
            });

            // Render based on type
            if (type === 'primary') {
                const primaryVariantClass = variant || 'ready';
                return (
                    <span key={idx} className={classNames('rm-card-primary-group', itemClassName)}>
                        <Text
                            className={classNames('bold', 'rm-card-primary', primaryVariantClass, {
                                'collapsed-primary': !isExpanded,
                            })}
                        >
                            {text}
                        </Text>
                        {continuation && (
                            <Text
                                className={classNames('rm-card-primary-continuation', {
                                    'collapsed-primary': !isExpanded,
                                })}
                            >
                                {continuation}
                            </Text>
                        )}
                    </span>
                );
            } else if (type === 'primary-small') {
                const primaryVariantClass = variant || 'ready';
                return (
                    <Text
                        key={idx}
                        className={classNames('bold', 'small', 'rm-card-primary', primaryVariantClass, itemClassName)}
                    >
                        {text}
                    </Text>
                );
            } else if (type === 'muted') {
                return renderMutedContent(text, idx, itemClassName);
            } else if (type === 'bold') {
                return (
                    <Text key={idx} className="semi-bold rm-card-title rm-card-item">
                        {text}
                    </Text>
                );
            } else {
                return (
                    <Text key={idx} className={itemClassName}>
                        {text}
                    </Text>
                );
            }
        };

        // Group consecutive inline items, render in order of definition in content array
        const result = [];
        let inlineGroup = [];

        filteredContent.forEach((item, idx) => {
            if (item.inline) {
                inlineGroup.push({ item, idx });
            } else {
                if (inlineGroup.length > 0) {
                    result.push(
                        <span key={`inline-${inlineGroup[0].idx}`} className="rm-card-inline-row">
                            {inlineGroup.map(({ item, idx }) => renderItem(item, idx))}
                        </span>,
                    );
                    inlineGroup = [];
                }
                result.push(renderItem(item, idx));
            }
        });

        if (inlineGroup.length > 0) {
            result.push(
                <span key={`inline-${inlineGroup[0].idx}`} className="rm-card-inline-row">
                    {inlineGroup.map(({ item, idx }) => renderItem(item, idx))}
                </span>,
            );
        }

        return <>{result}</>;
    };

    // render muted content with special colon handling
    const renderMutedContent = (text, idx, className) => {
        const colonIdx = typeof text === 'string' ? text.indexOf(':') : -1;

        if (colonIdx !== -1) {
            const before = text.slice(0, colonIdx + 1);
            const after = text.slice(colonIdx + 1);
            return (
                <span key={idx} className={classNames(className, 'with-padding-top-md')}>
                    <Text className="rm-card-muted-label">{before}</Text>
                    <Text className="semi-bold rm-card-muted-value">{after}</Text>
                </span>
            );
        } else {
            return (
                <Text key={idx} className={`rm-card-muted ${className}`}>
                    {text}
                </Text>
            );
        }
    };
    return (
        <RMCard
            variant={variant}
            className={classNames(className, { collapsed: !isExpanded })}
            showMoreIcon={false}
            {...props}
        >
            {onIgnoreOptimizations && showEllipsis && (
                <InventoryCardDropdown onIgnoreOptimizations={onIgnoreOptimizations} onMarkAsReady={onMarkAsReady} />
            )}
            {title && <Text className="rm-card-title rm-card-item block">{title}</Text>}
            {renderContent()}
            {isExpanded && children}
        </RMCard>
    );
};

InventoryCard.displayName = 'InventoryCard';

export default InventoryCard;
